import { FC } from 'react';
import styled, { css } from 'styled-components';

import Text from '@/components/Text';
import { VideoFactoryProjectStatus } from '@/utils/enums';

interface BadgeProps {
  $status: VideoFactoryProjectStatus;
}

export const Badge = styled.div<BadgeProps>`
  display: inline-block;
  padding: 2px 6px;
  padding-left: 14px;
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.warningLight};
  ${Text} {
    font-weight: 700;
    font-size: 9px;
    line-height: 12px;
    position: relative;
    color: ${({ theme }) => theme.colors.warning};
    &:before {
      content: '';
      width: 4px;
      height: 4px;
      left: -8px;
      background: ${({ theme }) => theme.colors.warning};
      top: calc(50% - 2px);
      border-radius: 50%;
      position: absolute;
    }
  }

  ${({ $status }) =>
    $status === VideoFactoryProjectStatus.COMPLETED &&
    css`
      background: ${({ theme }) => theme.colors.successLight};
      ${Text} {
        color: ${({ theme }) => theme.colors.success};
        &:before {
          background: ${({ theme }) => theme.colors.success};
        }
      }
    `}
  ${({ $status }) =>
    $status === VideoFactoryProjectStatus.FAILED &&
    css`
      background: ${({ theme }) => theme.colors.errorLight};
      ${Text} {
        color: ${({ theme }) => theme.colors.error};
        &:before {
          background: ${({ theme }) => theme.colors.error};
        }
      }
    `}
`;

const renderBadgeTitle = (status: VideoFactoryProjectStatus) => {
  switch (status) {
    case VideoFactoryProjectStatus.FAILED:
      return 'Failed';
    case VideoFactoryProjectStatus.COMPLETED:
      return 'Completed';
    case VideoFactoryProjectStatus.GENERATING_STORY:
      return 'Generating Story';
    case VideoFactoryProjectStatus.GENERATING_SCRIPT:
      return 'Generating Script';
    case VideoFactoryProjectStatus.GENERATING_IMAGES:
      return 'Generating Images';
    case VideoFactoryProjectStatus.GENERATING_VOICEOVERS:
      return 'Generating Voiceovers';
    case VideoFactoryProjectStatus.GENERATING_FINAL_VIDEO:
      return 'Generating Video';
    case VideoFactoryProjectStatus.GENERATING_IMAGE_VIDEOS:
      return 'Animating Images';
    case VideoFactoryProjectStatus.GENERATING_ANIMATED_IMAGES_VIDEO:
      return 'Generating Video';
    case VideoFactoryProjectStatus.DRAFT:
      return 'Draft';
    default:
      return 'N/A';
  }
};

interface Props {
  status: VideoFactoryProjectStatus;
}

const StatusBadge: FC<Props> = ({ status }) => {
  return (
    <Badge $status={status}>
      <Text $type="smallMedium">{renderBadgeTitle(status)}</Text>
    </Badge>
  );
};

export default StatusBadge;
